import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EmptyState, PageHeader } from "@/components/shared/Common";
import { api, download, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

const STATUS = {
  en_proceso: ["En proceso", "bg-[#fff6e0] text-[#8a5a00]"],
  completado: ["Completado", "bg-[#e9f7e1] text-[#1c6e00]"],
  error: ["Error", "bg-[#fdecea] text-[#b42318]"],
};
const fmtSize = (b) => (b == null ? "—" : b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

function BackupRow({ b, onRestore, locked }) {
  const [label, cls] = STATUS[b.status] || [b.status, ""];
  const get = async () => {
    try { await download(`/admin/backups/${b.id}/download`, {}, `${b.code}.zip`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid={`backup-row-${b.id}`} className="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-4 border-b border-[#f0f2ee] last:border-0">
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm">{b.code}
          <span data-testid={`backup-status-${b.id}`} className={`ml-2 rounded-full px-2 py-0.5 text-[10px] font-bold ${cls}`}>{label.toUpperCase()}</span></div>
        <div className="text-xs text-[#5b6656]">{fmtDateTime(b.created_at)} · {b.trigger === "automatico" ? "Automático" : b.trigger === "pre_restauracion" ? `Seguridad previa a restauración · ${b.created_by_name}` : `Manual · ${b.created_by_name}`}</div>
        {b.status === "completado" && <div className="text-xs text-[#8a9584]">{b.collections} tablas · {b.records} registros · {b.files} archivos · {fmtSize(b.size)}{b.files_missing ? ` · ${b.files_missing} archivos no disponibles` : ""}</div>}
        {b.error && <div className="text-xs text-[#b42318]">{b.error}</div>}
      </div>
      {b.status === "completado" && (
        <div className="flex gap-2">
          <Button data-testid={`backup-download-btn-${b.id}`} size="sm" variant="outline" className="rounded-full" onClick={get}><Download className="h-4 w-4 mr-1" />Descargar ZIP</Button>
          <Button data-testid={`backup-restore-btn-${b.id}`} size="sm" variant="outline" disabled={locked} className="rounded-full text-[#b42318] border-[#f5c2c0]" onClick={() => onRestore(b)}><RotateCcw className="h-4 w-4 mr-1" />Restaurar</Button>
        </div>
      )}
    </div>
  );
}

function RestoreDialog({ target, onClose, onDone }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setCode(""); }, [target]);
  const go = async () => {
    setBusy(true);
    try { await api.post(`/admin/backups/${target.id}/restore`, { confirm: code }); toast.success("Restauración iniciada"); onDone(); onClose(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="restore-dialog">
        <DialogHeader>
          <DialogTitle>Restaurar {target?.code}</DialogTitle>
          <DialogDescription>Se reemplazarán los datos de todos los departamentos, usuarios, contraseñas y auditoría por los del {fmtDateTime(target?.created_at)}. Antes se creará automáticamente un respaldo de seguridad del estado actual para poder deshacer.</DialogDescription>
        </DialogHeader>
        <label className="text-sm font-medium">Escriba <b>{target?.code}</b> para confirmar</label>
        <Input data-testid="restore-confirm-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder={target?.code} />
        <DialogFooter>
          <Button data-testid="restore-cancel-btn" variant="outline" className="rounded-full" onClick={onClose}>Cancelar</Button>
          <Button data-testid="restore-confirm-btn" disabled={busy || code.trim().toUpperCase() !== target?.code} className="rounded-full bg-[#b42318] hover:bg-[#912018]" onClick={go}>
            {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Restaurar sistema
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RestoreBanner({ r }) {
  if (!r) return null;
  const [txt, cls] = r.status === "en_proceso" ? [`Restaurando desde ${r.code}… no realice cambios hasta que termine.`, "bg-[#fff6e0] text-[#8a5a00]"]
    : r.status === "completado" ? [`Última restauración: ${r.code} por ${r.user_name} (${fmtDateTime(r.finished_at)}). Respaldo previo: ${r.safety_code}.`, "bg-[#e9f7e1] text-[#1c6e00]"]
    : [`La restauración desde ${r.code} falló: ${r.error}`, "bg-[#fdecea] text-[#b42318]"];
  return <div data-testid="restore-status-banner" className={`mb-4 rounded-xl px-4 py-3 text-sm ${cls}`}>{txt}</div>;
}

export default function Backups() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [restore, setRestore] = useState(null);
  const [target, setTarget] = useState(null);
  const load = () => Promise.all([api.get("/admin/backups"), api.get("/admin/backups/restores/latest")])
    .then(([a, b]) => { setItems(a.data); setRestore(b.data); }).catch(() => {});
  useEffect(() => { load(); }, []);
  const running = items.some((b) => b.status === "en_proceso") || restore?.status === "en_proceso";
  useEffect(() => { if (!running) return; const t = setInterval(load, 4000); return () => clearInterval(t); }, [running]);
  const create = async () => {
    setBusy(true);
    try { await api.post("/admin/backups"); toast.success("Respaldo iniciado"); load(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="backups-page">
      <PageHeader eyebrow="Administración" title="Respaldos" subtitle="Respaldo completo de todos los departamentos (datos y archivos) en un ZIP descargable. Se genera automáticamente cada 5 días y se conservan los últimos 12." />
      <RestoreBanner r={restore} />
      <div className="mb-4">
        <Button data-testid="backup-create-btn" disabled={busy || running} className="rounded-full" onClick={create}>
          {(busy || running) && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}{running ? "Proceso en curso…" : "Generar respaldo ahora"}
        </Button>
      </div>
      <div className="syc-card overflow-hidden" data-testid="backups-list">
        {items.length === 0 ? <EmptyState title="Aún no hay respaldos" /> : items.map((b) => <BackupRow key={b.id} b={b} locked={running} onRestore={setTarget} />)}
      </div>
      <RestoreDialog target={target} onClose={() => setTarget(null)} onDone={load} />
    </div>
  );
}
